// @flow
import { Map } from 'immutable';
import type { FieldConfig, FieldType, FieldsetConfig } from './FieldConfig';
import type { SearchFormValue } from './SearchForm';
import {
  getFieldEmptyValue,
  isFieldEmpty,
  serializeField,
  deserializeField,
} from './fieldUtils';
import { sortBy, startDate, endDate, clientId, catalog } from './config/fields.config';
import isDate from 'date-fns/is_date';

type FieldInfo = {
  type: FieldType,
  primary?: boolean,
  defaultValue: *,
};

export type FieldInfoMap = Map<string, FieldInfo>;

const TEXT_TYPE = { type: 'text' };
const CONSTANT_FIELDS = [sortBy, startDate, endDate, clientId, catalog];

const addField = (
  map: Map<string, FieldInfo>,
  field: FieldConfig,
  primary: boolean = false
) => {
  return map.set(field.name, {
    type: field.type,
    primary,
    defaultValue: field.defaultValue,
  });
};

export default class SearchFormHelper {
  static createFieldMap(
    primaryFieldsets: Array<FieldsetConfig>,
    extraFieldsets?: ?Array<FieldsetConfig>
  ): FieldInfoMap {
    return Map().withMutations(map => {
      primaryFieldsets.forEach(fieldset => {
        fieldset.fields.forEach(f => addField(map, f, true));
      });

      CONSTANT_FIELDS.forEach(field => addField(map, field, true));

      if (Array.isArray(extraFieldsets)) {
        extraFieldsets.forEach(fieldset =>
          fieldset.fields.forEach(f => addField(map, f))
        );
      }
    });
  }

  static bootstrapValues(
    infoMap: FieldInfoMap,
    defaultValue: SearchFormValue
  ): SearchFormValue {
    return defaultValue.withMutations(mp => {
      // for each field set defaultValue if unset
      infoMap.forEach((fieldInfo, name) => {
        const { type } = fieldInfo;
        const { defaultValue = getFieldEmptyValue(type) } = fieldInfo;

        if (!mp.has(name)) {
          mp.set(name, defaultValue);
        }
      });
    });
  }

  static sanitize(
    infoMap: FieldInfoMap,
    current: SearchFormValue,
    next: SearchFormValue
  ): SearchFormValue {
    // date range
    // if start_date chan
    const currentStartDate = current.get(startDate.name);
    const nextStartDate = next.get(startDate.name);
    const currentEndDate = current.get(endDate.name);
    const nextEndDate = next.get(endDate.name);
    let result = next;

    if (
      isDate(currentStartDate) &&
      isDate(nextStartDate) &&
      currentStartDate.getTime() !== nextStartDate.getTime()
    ) {
      if (
        isDate(nextEndDate) &&
        nextStartDate.getTime() > nextEndDate.getTime()
      ) {
        result = result.set(endDate.name, nextStartDate);
      }
    }

    // if endDate is changes
    if (
      isDate(currentEndDate) &&
      isDate(nextEndDate) &&
      currentEndDate.getTime() !== nextEndDate.getTime()
    ) {
      // endDate should not be less than startDate
      if (
        isDate(nextStartDate) &&
        nextStartDate.getTime() > nextEndDate.getTime()
      ) {
        result = result.set(startDate.name, nextEndDate);
      }
    }

    return result;
  }

  static clearHiddenFields(
    infoMap: FieldInfoMap,
    val: SearchFormValue
  ): SearchFormValue {
    const extras = infoMap.reduce((reduction, value, name) => {
      const { primary = false } = infoMap.get(name, TEXT_TYPE);

      if (primary === false) {
        reduction.push(name);
      }

      return reduction;
    }, []);

    return val.withMutations(map => {
      extras.forEach(name => map.remove(name));
    });
  }

  static serializeToObject(infoMap: FieldInfoMap, value: SearchFormValue) {
    return value.reduce((reduction, value, name) => {
      const { type: fieldType } = infoMap.get(name, TEXT_TYPE);

      if (value != null && !isFieldEmpty(fieldType, value)) {
        const serializedValue = serializeField(fieldType, value);

        if (serializedValue) {
          reduction[name] = serializedValue;
        }
      }

      return reduction;
    }, {});
  }

  static convertObjectToURI(obj: { [string]: string }): string {
    return Object.keys(obj)
      .reduce((acc, name) => {
        acc.push(`${name}=${encodeURIComponent(obj[name])}`);

        return acc;
      }, [])
      .join('&');
  }

  static serializeToURI(infoMap: FieldInfoMap, value: SearchFormValue) {
    return value
      .reduce((reduction, value, name) => {
        const { type: fieldType } = infoMap.get(name, TEXT_TYPE);
        const isEmpty = isFieldEmpty(fieldType, value);

        if (!isEmpty) {
          const serializedValue = serializeField(fieldType, value);

          if (serializedValue) {
            reduction.push(
              `${name}=${encodeURIComponent(String(serializedValue))}`
            );
          }
        }
        return reduction;
      }, [])
      .join('&');
  }

  static deserializeFromURI(infoMap: FieldInfoMap, value: string) {
    const entries = value
      .replace(/^\?/g, '')
      .split('&')
      .reduce((acc, pair) => {
        const [name, rawValue] = pair.split('=');

        if (name && value) {
          const { type: fieldType } = infoMap.get(name, TEXT_TYPE);
          const decodedValue = decodeURIComponent(rawValue);
          // if deserialization fails lets fallback to the value decodedValue
          // this is useful for fields that are not included the field map eg client_id
          const value =
            deserializeField(fieldType, decodedValue) || decodedValue;
          const notEmpty = !isFieldEmpty(fieldType, value);

          notEmpty && acc.push([name, value]);
        }

        return acc;
      }, []);

    return Map(entries);
  }

  fieldMap: FieldInfoMap;
  constructor(
    primaryFieldsets: Array<FieldsetConfig>,
    extraFieldsets?: ?Array<FieldsetConfig>
  ) {
    this.fieldMap = SearchFormHelper.createFieldMap(
      primaryFieldsets,
      extraFieldsets
    );
  }
  bootstrapValues(...rest: *) {
    return SearchFormHelper.bootstrapValues(this.fieldMap, ...rest);
  }
  sanitize(...rest: *) {
    return SearchFormHelper.sanitize(this.fieldMap, ...rest);
  }
  clearHiddenFields(...rest: *) {
    return SearchFormHelper.clearHiddenFields(this.fieldMap, ...rest);
  }
  serializeToObject(...rest: *) {
    return SearchFormHelper.serializeToObject(this.fieldMap, ...rest);
  }
  serializeToURI(...rest: *) {
    return SearchFormHelper.serializeToURI(this.fieldMap, ...rest);
  }
  deserializeFromURI(...rest: *) {
    return SearchFormHelper.deserializeFromURI(this.fieldMap, ...rest);
  }
}



// WEBPACK FOOTER //
// ./src/containers/search/SearchForm/SearchFormHelper.js